/* Options → Strategy Library sub-tab.
   Renders all 35 option strategies as filterable cards.

   Click a card → expands to a full detail drawer with the worked example,
   legs, pros/cons, risk profile, and (for the 12 supported strategies) an
   SVG payoff diagram + "Use this strategy" button that pre-fills the
   Trade Planner. */

import { useMemo, useState } from "react";
import { C } from "./_shared";
import { fetchPrice } from "./_prices";
import {
  STRATEGIES_LIB, STRATEGIES_BY_CATEGORY, CATEGORY_META,
  SENTIMENT_META, DIFFICULTY_META,
} from "./_strategyLibrary";

const FILTER_SENTIMENTS = [
  { k: "all",     l: "All"       },
  { k: "bullish", l: "🐂 Bullish" },
  { k: "bearish", l: "🐻 Bearish" },
  { k: "neutral", l: "↔️ Neutral" },
  { k: "vol",     l: "💥 Volatility" },
];
const FILTER_DIFFICULTY = [
  { k: "all",          l: "All" },
  { k: "beginner",     l: "🟢 Beginner" },
  { k: "intermediate", l: "🟡 Intermediate" },
  { k: "advanced",     l: "🔴 Advanced" },
];
const FILTER_RISK = [
  { k: "all",       l: "All"                },
  { k: "defined",   l: "🛡 Defined Risk"     },
  { k: "undefined", l: "⚠️ Undefined Risk"  },
];

export default function OptionsStrategyLibrary({ onUseStrategy }) {
  const [q, setQ] = useState("");
  const [sentiment, setSentiment] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [risk, setRisk] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return STRATEGIES_LIB.filter(s => {
      if (qn && !(s.name.toLowerCase().includes(qn) || (s.short || "").toLowerCase().includes(qn))) return false;
      if (sentiment !== "all") {
        // Match either exact or compound sentiment (e.g. "neutral_bullish" → bullish filter).
        const sKey = s.sentiment;
        if (sKey === sentiment) return true;
        if (sentiment === "bullish" && (sKey === "neutral_bullish" || sKey === "vol_bullish")) return true;
        if (sentiment === "bearish" && (sKey === "neutral_bearish" || sKey === "vol_bearish")) return true;
        if (sentiment === "vol"     && (sKey === "vol_bullish"     || sKey === "vol_bearish"))  return true;
        return false;
      }
      if (difficulty !== "all" && s.difficulty !== difficulty) return false;
      if (risk       !== "all" && s.risk       !== risk)       return false;
      return true;
    });
  }, [q, sentiment, difficulty, risk]);

  // Group the filtered result back by category so the UI shows category
  // headers between card groups — keeps orientation when user filters hard.
  const grouped = useMemo(() => {
    const out = {};
    for (const s of filtered) (out[s.category] ||= []).push(s);
    return out;
  }, [filtered]);

  return (
    <div data-testid="options-strategy-library" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <div style={{
        background: "linear-gradient(135deg,#eef2ff,#dbeafe)", border: "0.5px solid #c7d2fe",
        borderRadius: 10, padding: "12px 16px",
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#1e1b4b", marginBottom: 4 }}>📚 Strategy Library</div>
        <div style={{ fontSize: 11, color: "#3730a3" }}>
          35 option strategies with plain-English explanations, worked examples, and payoff diagrams.
          Click any card to expand. <b>12 strategies</b> can pre-fill the Trade Planner directly — more coming soon.
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "12px 14px",
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
      }}>
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search strategies…"
          data-testid="strategy-library-search"
          style={{
            flex: "1 1 200px", padding: "7px 11px", borderRadius: 6,
            border: "0.5px solid #d1d5db", fontSize: 12, background: "#fff",
          }}
        />
        <FilterGroup label="Sentiment"  value={sentiment}  onChange={setSentiment}  options={FILTER_SENTIMENTS} testid="strategy-library-sentiment"/>
        <FilterGroup label="Difficulty" value={difficulty} onChange={setDifficulty} options={FILTER_DIFFICULTY}  testid="strategy-library-difficulty"/>
        <FilterGroup label="Risk"       value={risk}       onChange={setRisk}       options={FILTER_RISK}        testid="strategy-library-risk"/>
        <div style={{ fontSize: 11, color: C.gray, marginLeft: "auto" }}>
          Showing <b style={{ color: "#111" }}>{filtered.length}</b> / {STRATEGIES_LIB.length} strategies
        </div>
      </div>

      {/* Grouped cards */}
      {filtered.length === 0 ? (
        <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: 24, textAlign: "center", color: C.gray, fontSize: 12 }}>
          No strategies match your filters.
        </div>
      ) : (
        Object.keys(CATEGORY_META).map(catKey => {
          const items = grouped[catKey];
          if (!items || items.length === 0) return null;
          const cat = CATEGORY_META[catKey];
          return (
            <div key={catKey}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#111", marginBottom: 8, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                {cat.icon} {cat.label} <span style={{ color: C.gray, fontWeight: 500 }}>({items.length})</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 10 }}>
                {items.map(s => (
                  <StrategyCard
                    key={s.id}
                    strategy={s}
                    isExpanded={expandedId === s.id}
                    onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                    onUseStrategy={onUseStrategy}
                  />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Filter pill group ────────────────────────────────────
function FilterGroup({ label, value, onChange, options, testid }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }} data-testid={testid}>
      <span style={{ fontSize: 10, color: C.gray, fontWeight: 600, marginRight: 3 }}>{label}:</span>
      {options.map(o => (
        <button
          key={o.k}
          onClick={() => onChange(o.k)}
          data-testid={`${testid}-${o.k}`}
          style={{
            padding: "4px 10px", borderRadius: 5, border: "0.5px solid",
            borderColor: value === o.k ? C.indigo : "#d1d5db",
            background: value === o.k ? C.ib : "#fff",
            color: value === o.k ? C.it : C.gray,
            fontSize: 10, fontWeight: 600, cursor: "pointer",
          }}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ── Strategy card ────────────────────────────────────────
function StrategyCard({ strategy, isExpanded, onToggle, onUseStrategy }) {
  const sm = SENTIMENT_META[strategy.sentiment] || SENTIMENT_META.neutral;
  const dm = DIFFICULTY_META[strategy.difficulty];
  const riskBadge = strategy.risk === "defined"
    ? { label: "🛡 Defined Risk",    color: "#16a34a", bg: "#dcfce7" }
    : { label: "⚠️ Undefined Risk", color: "#dc2626", bg: "#fee2e2" };
  const isUndefined = strategy.risk === "undefined";
  const hasPlanner = !!strategy.plannerKind;

  return (
    <div
      data-testid={`strategy-card-${strategy.id}`}
      style={{
        background: "#fff",
        border: `${isUndefined ? "1.5px" : "0.5px"} solid ${isUndefined ? "#fca5a5" : "#e5e7eb"}`,
        borderRadius: 10, overflow: "hidden",
        boxShadow: isExpanded ? "0 4px 14px rgba(99,102,241,0.15)" : "none",
        transition: "box-shadow 0.2s",
      }}
    >
      {/* Header (clickable) */}
      <button
        onClick={onToggle}
        data-testid={`strategy-card-toggle-${strategy.id}`}
        style={{
          width: "100%", border: "none", background: "transparent", textAlign: "left",
          padding: "12px 14px", cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#111" }}>{strategy.name}</div>
          <span style={{
            fontSize: 9, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap",
            background: dm.bg, color: dm.color, fontWeight: 700,
          }}>{dm.icon} {dm.label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{
            fontSize: 10, padding: "2px 7px", borderRadius: 4,
            background: sm.bg, color: sm.color, fontWeight: 700,
          }}>{sm.icon} {sm.label}</span>
          <span style={{
            fontSize: 10, padding: "2px 7px", borderRadius: 4,
            background: riskBadge.bg, color: riskBadge.color, fontWeight: 700,
          }}>{riskBadge.label}</span>
          <span style={{
            fontSize: 10, padding: "2px 7px", borderRadius: 4,
            background: strategy.net === "credit" ? "#dcfce7" : strategy.net === "debit" ? "#fef3c7" : "#f3f4f6",
            color:      strategy.net === "credit" ? "#14532d" : strategy.net === "debit" ? "#78350f" : C.gray,
            fontWeight: 700,
          }}>{strategy.net === "credit" ? "Net Credit" : strategy.net === "debit" ? "Net Debit" : "Mixed"}</span>
        </div>
        <div style={{ fontSize: 11, color: C.gray, lineHeight: 1.5 }}>{strategy.short}</div>
        <div style={{ fontSize: 10, color: C.indigo, marginTop: 6, fontWeight: 700 }}>
          {isExpanded ? "▼ Hide details" : "▶ Show details · worked example · payoff"}
        </div>
      </button>

      {/* Expanded drawer */}
      {isExpanded && (
        <StrategyExpandedPanel strategy={strategy} hasPlanner={hasPlanner} onUseStrategy={onUseStrategy}/>
      )}
    </div>
  );
}

// ── Expanded drawer ──────────────────────────────────────
function StrategyExpandedPanel({ strategy, hasPlanner, onUseStrategy }) {
  const [livePrice, setLivePrice] = useState(null);
  const [loading, setLoading] = useState(false);

  // Default sample ticker — something liquid so the example feels real.
  const SAMPLE_TICKER = "AAPL";

  async function loadLivePrice() {
    setLoading(true);
    try {
      const data = await fetchPrice(SAMPLE_TICKER);
      if (data?.ok) setLivePrice(data.price);
    } finally { setLoading(false); }
  }
  // Fire once on mount (non-blocking — example renders with fallback if slow).
  useMemo(() => { loadLivePrice(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const p = livePrice || 275;    // fallback realistic price
  const ex = strategy.example ? strategy.example(p) : null;

  return (
    <div style={{ padding: "14px 16px", borderTop: "0.5px solid #f3f4f6", background: "#fafbfc" }}>
      {strategy.risk === "undefined" && (
        <div style={{
          background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 7,
          padding: "8px 11px", marginBottom: 10, fontSize: 11, color: "#991b1b", fontWeight: 600,
        }}>
          ⚠️ <b>UNDEFINED RISK</b> — Advanced traders only. Potential loss is unlimited. Not recommended for beginners.
        </div>
      )}

      <SectionRow label="What it is"    body={strategy.whatIs}/>
      <SectionRow label="When to use"   body={strategy.whenToUse}/>

      {/* Legs */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#111", marginBottom: 4 }}>Legs</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {strategy.legs.map((leg, i) => {
            const sideTag = leg.side === "buy" ? "BUY" : leg.side === "sell" ? "SELL" : "LONG";
            const sideColor = leg.side === "buy" || leg.side === "long" ? "#16a34a" : "#dc2626";
            return (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11 }}>
                <span style={{
                  fontSize: 9, padding: "2px 6px", borderRadius: 4, fontWeight: 700,
                  background: sideColor + "22", color: sideColor, minWidth: 40, textAlign: "center",
                }}>{sideTag}</span>
                <span style={{ color: C.gray }}>
                  <b style={{ color: "#111", textTransform: "uppercase" }}>{leg.kind}</b> · {leg.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Risk profile */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 6, marginBottom: 12,
      }}>
        <StatPill label="Max Profit" value={strategy.maxProfit} color={strategy.maxProfit.includes("Unlimited") ? "#16a34a" : "#111"}/>
        <StatPill label="Max Loss"   value={strategy.maxLoss}   color={strategy.maxLoss.includes("UNLIMITED") ? "#dc2626" : "#111"}/>
        <StatPill label="Breakeven"  value={strategy.breakeven} color="#111"/>
      </div>

      {/* Worked example */}
      {ex && (
        <div style={{ background: "#fff", borderRadius: 7, border: "0.5px solid #e5e7eb", padding: "10px 12px", marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#111", marginBottom: 6 }}>
            📝 Worked example {loading ? "(loading live price…)" : livePrice ? `(live ${SAMPLE_TICKER} $${livePrice.toFixed(2)})` : "(indicative)"}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: C.gray, lineHeight: 1.6 }}>
            {ex.setup && <li><b style={{ color: "#111" }}>Setup:</b> {ex.setup}</li>}
            {ex.cost  && <li><b style={{ color: "#111" }}>Cost:</b> {ex.cost}</li>}
            {ex.win   && <li style={{ color: "#14532d" }}>✅ <b>Win:</b> {ex.win}</li>}
            {ex.lose  && <li style={{ color: "#7f1d1d" }}>❌ <b>Lose:</b> {ex.lose}</li>}
          </ul>
        </div>
      )}

      {/* Payoff diagram */}
      {strategy.payoff ? (
        <PayoffDiagram strategy={strategy} stockPrice={p}/>
      ) : (
        <div style={{
          background: "#fff", borderRadius: 7, border: "0.5px dashed #d1d5db",
          padding: "16px 12px", textAlign: "center", fontSize: 11, color: C.gray, fontStyle: "italic",
          marginBottom: 12,
        }}>
          🔒 Interactive payoff diagram — <b>planner support coming soon</b>. General shape: {strategy.sentiment.includes("bullish") ? "upward sloping right" : strategy.sentiment.includes("bearish") ? "downward sloping right" : strategy.sentiment.includes("vol") ? "V-shape (profits on big move)" : "inverted-V (profits in middle range)"}.
        </div>
      )}

      {/* Pros / Cons */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <ListBlock title="Pros" items={strategy.pros} color="#14532d" bg="#f0fdf4"/>
        <ListBlock title="Cons" items={strategy.cons} color="#7f1d1d" bg="#fef2f2"/>
      </div>

      {/* Use button */}
      <button
        onClick={() => hasPlanner && onUseStrategy && onUseStrategy(strategy)}
        disabled={!hasPlanner}
        data-testid={`strategy-use-${strategy.id}`}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 7, border: "none",
          background: hasPlanner ? C.indigo : "#f3f4f6",
          color: hasPlanner ? "#fff" : C.gray,
          fontSize: 12, fontWeight: 700, cursor: hasPlanner ? "pointer" : "not-allowed",
        }}
        title={hasPlanner ? "" : "This strategy requires Trade Planner extensions — coming in Phase 2"}
      >
        {hasPlanner ? `🎯 Use this strategy — open Trade Planner` : "🔒 Planner support coming soon"}
      </button>
    </div>
  );
}

// ── SVG payoff diagram ───────────────────────────────────
// Renders P/L at expiration across a ±25% price range around `stockPrice`.
// Uses `strategy.payoff(S, { strikes, prem })` to compute each sample.
function PayoffDiagram({ strategy, stockPrice }) {
  const W = 560, H = 180, PAD_X = 40, PAD_Y = 20;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;
  const strikes = strategy.defaultStrikes ? strategy.defaultStrikes(stockPrice) : [stockPrice];
  const prem    = strategy.defaultPrem    ? strategy.defaultPrem(stockPrice)    : 0;

  // Sample 80 points across ±25% of stock price.
  const lo = stockPrice * 0.75;
  const hi = stockPrice * 1.25;
  const samples = [];
  for (let i = 0; i < 80; i++) {
    const S = lo + (hi - lo) * (i / 79);
    samples.push({ S, pnl: strategy.payoff(S, { strikes, prem }) });
  }
  const maxAbs = Math.max(...samples.map(s => Math.abs(s.pnl)), 10);
  const xFor = (S)    => PAD_X + ((S - lo) / (hi - lo)) * innerW;
  const yFor = (pnl)  => PAD_Y + innerH / 2 - (pnl / maxAbs) * (innerH / 2);
  const zeroY = yFor(0);

  const pathD = samples.map((s, i) => `${i === 0 ? "M" : "L"} ${xFor(s.S).toFixed(1)} ${yFor(s.pnl).toFixed(1)}`).join(" ");
  const fillPath = `${pathD} L ${xFor(hi).toFixed(1)} ${zeroY.toFixed(1)} L ${xFor(lo).toFixed(1)} ${zeroY.toFixed(1)} Z`;

  // Current price marker
  const curX = xFor(stockPrice);

  return (
    <div style={{ background: "#fff", borderRadius: 7, border: "0.5px solid #e5e7eb", padding: "10px 12px", marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>📊 Payoff diagram at expiration</div>
        <div style={{ fontSize: 10, color: C.gray }}>Range: ${lo.toFixed(0)} → ${hi.toFixed(0)}</div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} data-testid={`payoff-svg-${strategy.id}`} style={{ display: "block" }}>
        <defs>
          <clipPath id={`clip-above-${strategy.id}`}><rect x="0" y="0"      width={W} height={zeroY}/></clipPath>
          <clipPath id={`clip-below-${strategy.id}`}><rect x="0" y={zeroY}  width={W} height={H - zeroY}/></clipPath>
        </defs>
        {/* Profit zone fill (green above zero) */}
        <path d={fillPath} fill="#16a34a33" clipPath={`url(#clip-above-${strategy.id})`}/>
        {/* Loss zone fill (red below zero) */}
        <path d={fillPath} fill="#dc262633" clipPath={`url(#clip-below-${strategy.id})`}/>
        {/* Zero line */}
        <line x1={PAD_X} y1={zeroY} x2={W - PAD_X} y2={zeroY} stroke="#9ca3af" strokeWidth="1" strokeDasharray="4,3"/>
        {/* Current price marker */}
        <line x1={curX} y1={PAD_Y} x2={curX} y2={H - PAD_Y} stroke="#4f46e5" strokeWidth="1.2" strokeDasharray="2,2"/>
        <text x={curX + 4} y={PAD_Y + 10} fill="#4f46e5" fontSize="9" fontWeight="700">Now ${stockPrice.toFixed(0)}</text>
        {/* Strike markers */}
        {strikes.map(k => (
          <g key={k}>
            <line x1={xFor(k)} y1={PAD_Y} x2={xFor(k)} y2={H - PAD_Y} stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="1,2" opacity="0.6"/>
            <text x={xFor(k) + 3} y={H - PAD_Y - 2} fill="#b45309" fontSize="8" fontWeight="600">${k}</text>
          </g>
        ))}
        {/* Payoff curve */}
        <path d={pathD} fill="none" stroke="#0f172a" strokeWidth="2"/>
        {/* Axis labels */}
        <text x={PAD_X} y={H - 4} fill="#9ca3af" fontSize="9">${lo.toFixed(0)}</text>
        <text x={W - PAD_X - 25} y={H - 4} fill="#9ca3af" fontSize="9">${hi.toFixed(0)}</text>
        <text x={4} y={PAD_Y + 8}  fill="#16a34a" fontSize="9" fontWeight="700">+${maxAbs.toFixed(0)}</text>
        <text x={4} y={H - PAD_Y}  fill="#dc2626" fontSize="9" fontWeight="700">-${maxAbs.toFixed(0)}</text>
      </svg>
      <div style={{ fontSize: 10, color: C.gray, marginTop: 4, fontStyle: "italic" }}>
        Payoff at expiration. Current stock price marker in indigo, strike(s) in amber. Above the dashed line = profit zone.
      </div>
    </div>
  );
}

// ── Small presentation helpers ───────────────────────────
function SectionRow({ label, body }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 11, color: C.gray, lineHeight: 1.6 }}>{body}</div>
    </div>
  );
}
function StatPill({ label, value, color }) {
  return (
    <div style={{ background: "#fff", borderRadius: 6, padding: "6px 9px", border: "0.5px solid #e5e7eb" }}>
      <div style={{ fontSize: 9, color: C.gray, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 800, color: color || "#111", marginTop: 2 }}>{value}</div>
    </div>
  );
}
function ListBlock({ title, items, color, bg }) {
  return (
    <div style={{ background: bg, borderRadius: 6, padding: "8px 10px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color, marginBottom: 3 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 14, fontSize: 10, color, lineHeight: 1.5 }}>
        {(items || []).map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
